import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug) as Article | undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-2">文章不存在</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.imageUrl,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '一本道',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yibendao.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${article.title} - 一本道`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},一本道,${article.category},一本道文章`}
        canonicalUrl={`https://yibendao.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <SchemaOrg schema={articleSchema} />

      <Navbar />
      <main className="pt-20 pb-16">
        <article className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="mt-6 mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-0.5 rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors duration-200"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              <span className="text-xs text-foreground-300">·</span>
              <span className="text-xs text-foreground-400">{article.publishedAt}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mt-6 pb-8 border-b border-background-200">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <span className="text-sm font-medium text-foreground-800">{article.author}</span>
                <p className="text-xs text-foreground-400">一本道作者</p>
              </div>
            </div>
          </header>

          <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose-custom text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-500 bg-background-100 border border-background-200/70 px-3 py-1 rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative w-full h-36 rounded-lg overflow-hidden mb-3">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 leading-snug line-clamp-2">
                      {related.title}
                    </h4>
                    <span className="text-xs text-foreground-400 mt-1 block">{related.readTime} 分钟阅读</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}